/**
 * Checks if input looks like a URL
 */
export const looksLikeUrl = (input: string): boolean => {
  if (!input) return false;
  const trimmed = input.trim().toLowerCase();
  
  // Must have protocol or www prefix to be a URL
  if (trimmed.includes('http://') || trimmed.includes('https://') || trimmed.startsWith('www.')) {
    return true;
  }
  
  // Check for domain patterns - but only if it looks like a URL path (contains /)
  // This allows usernames like "viraluj.pl" while still catching "instagram.com/user"
  const hasDomainExtension = trimmed.includes('.com') || 
                              trimmed.includes('.pl') || 
                              trimmed.includes('.net') || 
                              trimmed.includes('.org');
  
  if (hasDomainExtension && trimmed.includes('/')) {
    return true;
  }

  return false;
};

/**
 * Checks if input is a valid Instagram URL
 * Accepts: instagram.com, www.instagram.com, https://instagram.com, etc.
 */
export const isInstagramUrl = (input: string): boolean => {
  if (!input) return false;
  const trimmed = input.trim().toLowerCase();
  return trimmed.includes('instagram.com');
};

/**
 * Extracts Instagram username from URL or direct input
 * Handles formats like:
 * - https://www.instagram.com/username
 * - https://instagram.com/username/
 * - www.instagram.com/username
 * - instagram.com/username
 * - @username
 * - username
 */
export const extractInstagramUsername = (input: string): string => {
  if (!input) return '';
  
  const trimmed = input.trim();
  
  // Try to match Instagram URL pattern (with or without protocol)
  const urlMatch = trimmed.match(/(?:https?:\/\/)?(?:www\.)?instagram\.com\/([a-zA-Z0-9._]+)/i);
  if (urlMatch) {
    return urlMatch[1].toLowerCase();
  }
  
  // If it looks like a URL but not Instagram, return empty (invalid)
  if (looksLikeUrl(trimmed)) {
    return '';
  }
  
  // If not a URL, clean the input and treat as username
  // Remove @ symbol and convert to lowercase
  const cleaned = trimmed.replace(/^@+/, '').toLowerCase();
  
  // Validate basic username format
  if (/^[a-zA-Z0-9._]+$/.test(cleaned)) {
    return cleaned;
  }
  
  return '';
};

/**
 * Validates if the extracted username is valid Instagram format
 * Instagram usernames can only contain: letters, numbers, periods, underscores
 */
export const isValidInstagramUsername = (username: string): boolean => {
  if (!username || username.length === 0) return false;
  if (username.length > 30) return false;
  return /^[a-zA-Z0-9._]+$/.test(username);
};

/**
 * Sentinel values written to user_profiles.instagram_username by past code
 * paths when the column had to satisfy NOT NULL but no real username was
 * available yet. Treat these as "missing" everywhere so the user can be
 * routed back to step 1 to set a real username — Settings can only ADD a
 * username when none exists, so a row stuck on a sentinel is unrecoverable
 * from the UI without going through onboarding again.
 */
const PLACEHOLDER_INSTAGRAM_USERNAMES = new Set(["pending_setup", "unknown"]);

/**
 * Returns true if the stored value is a real, user-confirmed Instagram
 * username (not empty, null, or one of the legacy sentinels).
 */
export const isRealInstagramUsername = (
  value: string | null | undefined,
): boolean => {
  if (!value) return false;
  const trimmed = value.trim();
  if (trimmed.length === 0) return false;
  return !PLACEHOLDER_INSTAGRAM_USERNAMES.has(trimmed);
};

/**
 * Validates Instagram input and returns error message if invalid
 * Accepts: Instagram URLs or direct usernames
 * Rejects: Non-Instagram URLs
 */
export const validateInstagramInput = (input: string): string => {
  if (!input || input.trim() === '') return '';
  
  const trimmed = input.trim();
  
  // If it looks like a URL, it must be an Instagram URL
  if (looksLikeUrl(trimmed)) {
    if (!isInstagramUrl(trimmed)) {
      return 'Akceptujemy tylko linki z Instagram (instagram.com)';
    }
    
    // Extract username from Instagram URL
    const username = extractInstagramUsername(trimmed);
    if (!username) {
      return 'Nieprawidłowy format linku Instagram. Użyj: instagram.com/nazwa_użytkownika';
    }
    
    if (!isValidInstagramUsername(username)) {
      return 'Nazwa użytkownika może zawierać tylko litery, cyfry, kropki (.) i podkreślniki (_)';
    }
    
    if (username.length > 30) {
      return 'Nazwa użytkownika musi mieć maksymalnie 30 znaków';
    }
    
    return '';
  }
  
  // Check for @ at the beginning - just remove it
  const cleaned = trimmed.replace(/^@+/, '');
  
  // Check for spaces
  if (cleaned.includes(' ')) {
    return 'Nazwa użytkownika nie może zawierać spacji';
  }
  
  // Check if it's an email (common mistake)
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleaned)) {
    return 'To wygląda jak adres email, nie nazwa użytkownika Instagram';
  }
  
  // Validate username format
  if (!isValidInstagramUsername(cleaned)) {
    return 'Nazwa użytkownika może zawierać tylko litery, cyfry, kropki (.) i podkreślniki (_)';
  }
  
  // Check length
  if (cleaned.length > 30) {
    return 'Nazwa użytkownika musi mieć maksymalnie 30 znaków';
  }
  
  return '';
};
